import { purchaseApiAxiosInstance } from '@/services/purchaseApiAxiosInstance'
import localForageLib from 'localforage'
import { defineStore } from 'pinia'
import { ref } from 'vue'

const DEFAULT_SECRET = '3219617972278624'

export enum EncryptionErrorType {
	NeedSecret = 'need secret',
	WrongSecret = 'wrong secret',
	KeyNotFound = 'key not found',
	DecryptionError = 'decryption error',
	EncryptionError = 'encryption error',
	KeyNotSaved = 'key not saved',
	NeedServerKey = 'need server key',
}

export type EncryptionStatus = EncryptionErrorType | 'unencrypted'

export class EncryptionError extends Error {
	public errorType: EncryptionErrorType

	constructor(errorType: EncryptionErrorType) {
		super(errorType)
		this.errorType = errorType
	}
}

export function isEncryptionError(error: unknown): error is EncryptionError {
	return Object.hasOwnProperty.call(error, 'errorType')
}

type ServerKeyResponse = {
	keyId: string
	publicKey: string
	createdAt: string
}

export type DataEncryptionKey = {
	keyId: string
	/** AES-GCM 256 key material */
	rawDek: ArrayBuffer

	secretEncodedKey: string | null
	secretSalt: string | null

	serverEncodedKey: string | null
	/** id of the key pair used to encode the DEK */
	serverKeyId: string | null
}

export type StoredDataEncryptionKey = Omit<DataEncryptionKey, 'rawDek'>

/** Struct for saving encrypted data.
 * Both the iv and keyId are required to decrypt.
 */
export type EncryptedData = {
	ciphertext: string
	iv: string
	keyId: string
}

/** Store for managing encryption on a per-plan basis */
export const useEncryptionStore = defineStore('encryption', () => {
	// let initializedPromise: Promise<void> | undefined = undefined
	const localKeyStore = localForageLib.createInstance({
		name: 'encryption',
		storeName: 'localKeys',
	})

	const serverKeyStore = localForageLib.createInstance({
		name: 'encryption',
		storeName: 'serverKeys',
	})

	const dataEncryptionKeys = ref<DataEncryptionKey[]>([])
	const serverPublicKey = ref<ServerKeyResponse | null>(null)

	/** Get the latest server key and store in localForage */
	async function fetchUpdatedServerKey() {
		const { data } =
			await purchaseApiAxiosInstance.get<ServerKeyResponse>('/v1/keys/current')

		serverPublicKey.value = data
		serverKeyStore.setItem('current', data)
	}

	/** Get the latest server key from localForage */
	async function loadServerKey() {
		const savedKey = await serverKeyStore.getItem<ServerKeyResponse>('current')
		if (
			savedKey &&
			(!serverPublicKey.value ||
				savedKey.createdAt > serverPublicKey.value.createdAt)
		) {
			serverPublicKey.value = savedKey
		}
	}

	/** Create a new data encryption key. Encrypt it and store in localForage */
	async function createDek(
		keyId: string,
		secret: string = DEFAULT_SECRET
	): Promise<DataEncryptionKey> {
		let serverEncodedKey: string | null = null
		let secretEncodedKey: string | null = null
		let secretSalt: string | null = null

		// Generate key
		const dek = await crypto.subtle.generateKey(
			{ name: 'AES-GCM', length: 256 },
			true,
			['encrypt', 'decrypt']
		)

		const rawDek = await crypto.subtle.exportKey('raw', dek)

		// Encrypt w/ server key
		if (serverPublicKey.value) {
			serverEncodedKey = await encodeServerKey(
				serverPublicKey.value.publicKey,
				rawDek
			)
		}

		// Encrypt w/ secret
		if (secret) {
			const { secretEncodedKey: key, salt } = await encodeSecretKey(
				rawDek,
				secret
			)
			secretEncodedKey = key
			secretSalt = salt
		}

		// Store
		const dekRecord: StoredDataEncryptionKey = {
			keyId,
			serverEncodedKey,
			serverKeyId: serverPublicKey.value?.keyId ?? null,
			secretEncodedKey,
			secretSalt,
		}

		const fullDek = { ...dekRecord, rawDek }

		await localKeyStore.setItem<StoredDataEncryptionKey>(keyId, dekRecord)
		dataEncryptionKeys.value.push(fullDek)

		return fullDek
	}

	/** Replace the secret with the default */
	async function removeSecret(keyId: string) {
		return setSecret(keyId, DEFAULT_SECRET)
	}

	/** Encrypt a secret-encoded backup to the key and save in localForage */
	async function setSecret(keyId: string, secret: string, oldSecret?: string) {
		if (!secret) secret = DEFAULT_SECRET

		const key = await prepareDek(keyId, false, oldSecret)
		const { secretEncodedKey, salt } = await encodeSecretKey(key.rawDek, secret)

		dataEncryptionKeys.value = dataEncryptionKeys.value.map((k) => {
			if (k.keyId === keyId) {
				return {
					...k,
					secretEncodedKey,
					secretSalt: salt,
				}
			}
			return k
		})

		await localKeyStore.setItem<StoredDataEncryptionKey>(keyId, {
			...key,
			secretEncodedKey,
			secretSalt: salt,
		})
	}

	/** Encrypt data using the specified data encryption key by id */
	async function encrypt(
		keyId: string,
		data: string,
		secret?: string
	): Promise<EncryptedData> {
		// Load or create key
		const key = await prepareDek(keyId, true, secret)

		// Import DEK as CryptoKey
		const cryptoKey = await crypto.subtle.importKey(
			'raw',
			key.rawDek,
			{ name: 'AES-GCM' },
			false,
			['encrypt']
		)

		// Create random IV
		const iv = crypto.getRandomValues(new Uint8Array(12))

		// Encrypt
		const encoder = new TextEncoder()
		const encoded = encoder.encode(data)
		const encrypted = await crypto.subtle.encrypt(
			{ name: 'AES-GCM', iv },
			cryptoKey,
			encoded
		)

		return {
			keyId,
			ciphertext: base64encode(encrypted),
			iv: base64encode(iv.buffer),
		}
	}

	/** Decrypt data using the specified data encryption key by id */
	async function decrypt(
		encryptedData: EncryptedData,
		secret: string = DEFAULT_SECRET
	): Promise<string> {
		const key = await prepareDek(encryptedData.keyId, false, secret)

		// Convert base64 inputs to buffers
		const ivBuf = base64decode(encryptedData.iv)
		const cipherBuf = base64decode(encryptedData.ciphertext)

		// Import DEK
		const cryptoKey = await crypto.subtle.importKey(
			'raw',
			key.rawDek,
			{ name: 'AES-GCM' },
			false,
			['decrypt']
		)

		// Decrypt
		const decrypted = await crypto.subtle.decrypt(
			{ name: 'AES-GCM', iv: ivBuf },
			cryptoKey,
			cipherBuf
		)

		const plaintext = new TextDecoder().decode(decrypted)

		return plaintext
	}

	/** TESTS ONLY: Clear the DEKs from memory */
	function testClearMemory() {
		dataEncryptionKeys.value = []
	}

	/*** Helpers ***/
	async function prepareDek(
		keyId: string,
		createIfNotFound: boolean,
		secret: string = DEFAULT_SECRET
	): Promise<DataEncryptionKey> {
		// Load key from memory
		let key = dataEncryptionKeys.value.find((k) => k.keyId === keyId)

		if (!key) {
			// Load key from storage
			key = await loadLocalKey(keyId, secret)

			if (!key && createIfNotFound) {
				key = await createDek(keyId, secret)
			}

			if (!key) {
				throw new EncryptionError(EncryptionErrorType.KeyNotFound)
			}
		}

		// Check to see if we need to add backups
		if (!serverPublicKey.value) {
			try {
				await loadServerKey()
			} catch (error) {
				console.warn('Could not load server key from storage', error)
			}
		}
		if (!key.serverEncodedKey && serverPublicKey.value) {
			key.serverEncodedKey = await encodeServerKey(
				serverPublicKey.value.publicKey,
				key.rawDek
			)
			key.serverKeyId = serverPublicKey.value.keyId
		}

		if (!key.secretEncodedKey && secret) {
			const { secretEncodedKey, salt } = await encodeSecretKey(
				key.rawDek,
				secret
			)
			key.secretEncodedKey = secretEncodedKey
			key.secretSalt = salt
		}

		// Update memory
		const { rawDek, ...keyToStore } = key

		dataEncryptionKeys.value = dataEncryptionKeys.value.filter(
			(k) => k.keyId !== keyId
		)
		dataEncryptionKeys.value.push(key)

		await localKeyStore.setItem<StoredDataEncryptionKey>(keyId, keyToStore)

		return key
	}

	/** Load and decrypt DEK from localForage */
	async function loadLocalKey(
		keyId: string,
		secret?: string
	): Promise<DataEncryptionKey | undefined> {
		let rawDek: ArrayBuffer | null = null

		// Find item
		const keyObj = await localKeyStore.getItem<StoredDataEncryptionKey>(keyId)
		if (!keyObj) {
			return undefined
		}

		// Decrypt from secret
		if (keyObj.secretEncodedKey && secret) {
			try {
				rawDek = await decodeSecretKey(
					keyObj.secretEncodedKey,
					keyObj.secretSalt!,
					secret
				)
			} catch {
				if (secret !== DEFAULT_SECRET) {
					console.warn('Wrong secret used')
				}
			}
		}

		// Fallback: decrypt from server
		if (!rawDek && keyObj.serverEncodedKey && keyObj.serverKeyId) {
			try {
				rawDek = await decodeServerKey(
					keyObj.serverEncodedKey,
					keyObj.serverKeyId
				)
			} catch (error) {
				console.warn('Could not access server for decryption', error)
			}
		}

		if (!rawDek) {
			if (keyObj.secretEncodedKey && secret === DEFAULT_SECRET) {
				throw new EncryptionError(EncryptionErrorType.NeedSecret)
			} else if (keyObj.secretEncodedKey) {
				throw new EncryptionError(EncryptionErrorType.WrongSecret)
			} else if (keyObj.serverEncodedKey) {
				throw new EncryptionError(EncryptionErrorType.NeedServerKey)
			} else {
				throw new EncryptionError(EncryptionErrorType.KeyNotSaved)
			}
		}

		const dataEncryptionKey = {
			...keyObj,
			rawDek,
		}

		dataEncryptionKeys.value = dataEncryptionKeys.value.filter(
			(k) => k.keyId !== keyId
		)
		dataEncryptionKeys.value.push(dataEncryptionKey)

		return dataEncryptionKey
	}

	/** Return the current encryption key for the matching plan Id*/
	function findLocalKeyById(
		planId: string
	): StoredDataEncryptionKey | undefined {
		const foundRecord = dataEncryptionKeys.value.find(
			(record) => record.keyId === planId
		)
		if (foundRecord) {
			const { rawDek, ...localKey } = foundRecord
			return localKey
		}

		return undefined
	}

	/** Encode binary data as base64 so it can be stored */
	function base64encode(buf: ArrayBuffer): string {
		return btoa(String.fromCharCode(...new Uint8Array(buf)))
	}

	/** Decode base64 data so it can be used by the encryption methods */
	function base64decode(str: string): ArrayBuffer {
		return Uint8Array.from(atob(str), (c) => c.charCodeAt(0))
	}

	/** Retrieve the DEK using a secret */
	async function decodeSecretKey(
		secretEncodedKey: string,
		saltStr: string,
		secret: string
	): Promise<ArrayBuffer> {
		const salt = new Uint8Array(base64decode(saltStr))
		const encrypted = base64decode(secretEncodedKey)

		const derivedKey = await deriveKeyFromSecret(secret, salt)

		try {
			const rawDek = await crypto.subtle.decrypt(
				{ name: 'AES-GCM', iv: salt },
				derivedKey,
				encrypted
			)

			return rawDek
		} catch (e) {
			throw new EncryptionError(
				secret === DEFAULT_SECRET
					? EncryptionErrorType.NeedSecret
					: EncryptionErrorType.WrongSecret
			)
		}
	}

	/** Encrypt the DEK with a secret */
	async function encodeSecretKey(
		rawDek: ArrayBuffer,
		secret: string
	): Promise<{ salt: string; secretEncodedKey: string }> {
		const salt = crypto.getRandomValues(new Uint8Array(16))
		const derived = await deriveKeyFromSecret(secret, salt)
		const encrypted = await crypto.subtle.encrypt(
			{ name: 'AES-GCM', iv: salt },
			derived,
			rawDek
		)
		const secretEncodedKey = base64encode(encrypted)
		const saltStr = base64encode(salt.buffer)

		return {
			salt: saltStr,
			secretEncodedKey,
		}
	}

	/** Create a key from a secret. This key will be used to decrypt the DEK */
	async function deriveKeyFromSecret(
		secret: string,
		salt: Uint8Array
	): Promise<CryptoKey> {
		// We use SSN so remove all non-digits from secret
		secret = secret.replace(/[^0-9]/g, '')
		const enc = new TextEncoder()
		const baseKey = await crypto.subtle.importKey(
			'raw',
			enc.encode(secret),
			'PBKDF2',
			false,
			['deriveKey']
		)

		return crypto.subtle.deriveKey(
			{
				name: 'PBKDF2',
				salt,
				iterations: 250_000,
				hash: 'SHA-256',
			},
			baseKey,
			{ name: 'AES-GCM', length: 256 },
			true,
			['encrypt', 'decrypt']
		)
	}

	/**
	 * Encrypt the DEK with the server's public key
	 *
	 * @param serverPublicKey Public key in ASN.1 text format with headers and footers
	 * */
	async function encodeServerKey(
		serverPublicKey: string,
		rawDek: ArrayBuffer
	): Promise<string> {
		const serverKeySpki = base64decode(
			serverPublicKey.replace(/-----(BEGIN|END) PUBLIC KEY-----|\n/g, '')
		)
		const serverKey = await crypto.subtle.importKey(
			'spki',
			serverKeySpki,
			{ name: 'RSA-OAEP', hash: 'SHA-256' },
			false,
			['encrypt']
		)

		const serverEncodedKeyBuf = await crypto.subtle.encrypt(
			{ name: 'RSA-OAEP' },
			serverKey,
			rawDek
		)
		const serverEncodedKey = base64encode(serverEncodedKeyBuf)
		return serverEncodedKey
	}

	/** Ask the server to decrypt the DEK */
	async function decodeServerKey(
		serverEncodedKey: string,
		serverKeyId: string
	): Promise<ArrayBuffer> {
		const response = await purchaseApiAxiosInstance.post(
			'/v1/keys/' + serverKeyId + '/decrypt',
			{
				ciphertext: serverEncodedKey,
			}
		)

		return base64decode(response.data.plaintext)
	}

	async function deleteEncryptionRecord(keyId: string) {
		await localKeyStore.removeItem(keyId)

		dataEncryptionKeys.value = dataEncryptionKeys.value.filter(
			(k) => k.keyId !== keyId
		)
	}

	async function storeDek(key: StoredDataEncryptionKey) {
		await localKeyStore.setItem(key.keyId, key)
	}

	return {
		createDek,
		findLocalKeyById,
		dataEncryptionKeys,
		decrypt,
		deleteEncryptionRecord,
		encrypt,
		fetchUpdatedServerKey,
		removeSecret,
		setSecret,
		testClearMemory,
		storeDek,
	}
})
